import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const GOLD = '#C9A84C';
const BG = '#050810';
const SURFACE = '#0B1120';
const BORDER = 'rgba(255,255,255,0.07)';
const OFF_WHITE = '#A0AABB';
const RED = '#FF4444';
const GREEN = '#22C55E';

const s = {
  page: {
    background: BG,
    color: '#fff',
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    minHeight: '100vh',
  },
  wrap: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '72px 24px 80px',
  },
  badge: {
    display: 'inline-block',
    background: 'rgba(201,168,76,0.12)',
    border: '1px solid rgba(201,168,76,0.3)',
    color: GOLD,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '6px 14px',
    borderRadius: 20,
    marginBottom: 24,
  },
  h1: {
    fontSize: 'clamp(28px,5vw,42px)',
    fontWeight: 800,
    margin: '0 0 16px',
    lineHeight: 1.15,
  },
  sub: {
    color: OFF_WHITE,
    fontSize: 17,
    lineHeight: 1.7,
    margin: '0 0 56px',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: OFF_WHITE,
    marginBottom: 20,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 48,
  },
  thMane: {
    background: 'rgba(201,168,76,0.1)',
    color: GOLD,
    fontWeight: 700,
    fontSize: 13,
    padding: '14px 20px',
    textAlign: 'left',
    border: '1px solid rgba(201,168,76,0.2)',
    borderBottom: `2px solid ${GOLD}`,
  },
  thComp: {
    background: SURFACE,
    color: OFF_WHITE,
    fontWeight: 700,
    fontSize: 13,
    padding: '14px 20px',
    textAlign: 'left',
    border: `1px solid ${BORDER}`,
  },
  thLabel: {
    background: 'transparent',
    color: OFF_WHITE,
    fontWeight: 600,
    fontSize: 12,
    padding: '14px 0',
    textAlign: 'left',
    letterSpacing: '0.04em',
    border: 'none',
    borderBottom: `1px solid ${BORDER}`,
  },
  tdLabel: {
    color: OFF_WHITE,
    fontSize: 14,
    padding: '16px 0',
    borderBottom: `1px solid ${BORDER}`,
    fontWeight: 500,
    verticalAlign: 'top',
  },
  tdMane: {
    color: '#fff',
    fontSize: 14,
    padding: '16px 20px',
    borderBottom: '1px solid rgba(201,168,76,0.15)',
    background: 'rgba(201,168,76,0.04)',
    border: '1px solid rgba(201,168,76,0.1)',
    verticalAlign: 'top',
  },
  tdComp: {
    color: OFF_WHITE,
    fontSize: 14,
    padding: '16px 20px',
    borderBottom: `1px solid ${BORDER}`,
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    verticalAlign: 'top',
  },
  callout: {
    background: 'rgba(201,168,76,0.07)',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: 12,
    padding: '28px 32px',
    marginBottom: 48,
  },
  calloutLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: GOLD,
    marginBottom: 12,
  },
  calloutText: {
    color: '#fff',
    fontSize: 16,
    lineHeight: 1.8,
    margin: 0,
  },
  faqItem: {
    borderBottom: `1px solid ${BORDER}`,
    paddingBottom: 28,
    marginBottom: 28,
  },
  faqQ: {
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    marginBottom: 10,
  },
  faqA: {
    color: OFF_WHITE,
    fontSize: 15,
    lineHeight: 1.7,
    margin: 0,
  },
  cta: {
    background: 'rgba(201,168,76,0.07)',
    border: '1px solid rgba(201,168,76,0.25)',
    borderRadius: 16,
    padding: '48px 40px',
    textAlign: 'center',
    marginTop: 56,
  },
  ctaTitle: {
    fontSize: 24,
    fontWeight: 800,
    marginBottom: 12,
  },
  ctaSub: {
    color: OFF_WHITE,
    fontSize: 15,
    lineHeight: 1.6,
    marginBottom: 32,
  },
  btn: {
    display: 'inline-block',
    background: GOLD,
    color: '#000',
    fontWeight: 800,
    fontSize: 14,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '16px 40px',
    borderRadius: 8,
    textDecoration: 'none',
  },
};

const rows = [
  {
    label: 'Monthly Base Price',
    mane: '$29/mo — locked forever',
    comp: <span>$25/mo (single) to $85/mo (premium)<br /><span style={{ fontSize: 12, color: OFF_WHITE }}>Most pros need the $45-65/mo tier</span></span>,
  },
  {
    label: 'Commission on Bookings',
    mane: <span style={{ color: GREEN }}>0% — always</span>,
    comp: <span>No direct commission, but add-on fees stack up fast</span>,
  },
  {
    label: 'Payment Processing',
    mane: <span style={{ color: GREEN }}>Standard Stripe rates (2.9% + 30c)</span>,
    comp: <span style={{ color: RED }}>2.75% + proprietary processor<br /><span style={{ fontSize: 12 }}>Must use Vagaro's payment system</span></span>,
  },
  {
    label: 'Feature Paywalls',
    mane: <span style={{ color: GREEN }}>All features included at $29/mo</span>,
    comp: <span style={{ color: RED }}>Website, email marketing, forms, payroll — all separate add-ons ($10-30/mo each)</span>,
  },
  {
    label: 'Client Data Ownership',
    mane: <span style={{ color: GREEN }}>Yours. Export anytime.</span>,
    comp: <span style={{ color: RED }}>Limited export. Clients tied to Vagaro ecosystem.</span>,
  },
  {
    label: 'Client App Requirement',
    mane: <span style={{ color: GREEN }}>No — books from any browser</span>,
    comp: <span style={{ color: RED }}>Vagaro app required for full experience</span>,
  },
  {
    label: 'AI Re-Engagement',
    mane: <span style={{ color: GREEN }}>Included — auto win-back messages</span>,
    comp: <span style={{ color: RED }}>Not available</span>,
  },
  {
    label: 'SMS Reminders',
    mane: <span style={{ color: GREEN }}>Included</span>,
    comp: 'Included in higher tiers',
  },
  {
    label: 'Contract',
    mane: 'Month-to-month',
    comp: 'Month-to-month',
  },
];

const faqs = [
  {
    q: 'How much does Vagaro actually cost per month?',
    a: "Vagaro starts at $25/mo for a single user, but that's the stripped-down version. Most independent pros end up on the $45-65/mo tier to get the features they need. Then add $10/mo for a website, $10/mo for forms, $10/mo for email marketing. A fully-featured Vagaro setup regularly hits $85-100/mo. Mane is $29/mo with everything included.",
  },
  {
    q: 'Can I use my own payment processor with Vagaro?',
    a: "No. Vagaro requires you to use their proprietary payment processing system. You can't bring your own Stripe or Square reader. This locks you into their ecosystem and makes switching painful. With Mane, payments run through standard Stripe — an account you own.",
  },
  {
    q: "Is Vagaro good for independent professionals?",
    a: "Vagaro was built for salons and multi-chair shops. If you're a solo pro or suite renter, you're paying for team management features you'll never use. Their pricing tiers are designed around employee counts. Mane is built from the ground up for the independent professional.",
  },
  {
    q: "What does Vagaro have that Mane doesn't?",
    a: "Vagaro has a marketplace where clients can discover you, payroll for teams, and inventory management. If you run a 10-chair salon with employees, Vagaro makes more sense. If you're an independent professional who already has clients and wants a clean, affordable booking system without the bloat, Mane is built for you.",
  },
  {
    q: "What happens to my price if Mane raises rates?",
    a: "Founding members are locked at $29/mo for life. That's in your membership agreement. When we raise prices for new members, your rate doesn't move. Ever.",
  },
];

export default function VsVagaro() {
  return (
    <div style={s.page}>
      <Helmet>
        <title>Mane vs. Vagaro — The Real Cost Breakdown</title>
        <meta name="description" content="Vagaro starts at $25/mo but the add-ons pile up. Website, forms, email marketing, payroll — all extra. Here's what you'll actually pay vs. Mane's flat $29/mo." />
        <meta property="og:title" content="Mane vs. Vagaro — The Real Cost Breakdown" />
        <meta property="og:description" content="Vagaro's base price hides a wall of add-on fees. Here's the honest math — and why independent pros are switching to Mane." />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://joinmane.com/og-image-v8.png" />
      </Helmet>
      <div style={s.wrap}>
        <div style={s.badge}>Mane vs. Vagaro</div>
        <h1 style={s.h1}>Vagaro's base price is just the beginning.</h1>
        <p style={s.sub}>
          Vagaro advertises $25/mo. What they don't mention: website builder is extra.
          Email marketing is extra. Custom forms are extra. By the time you have what you need,
          you're paying $85-100/mo. Here's the full picture.
        </p>

        <div style={s.sectionTitle}>Side-by-side breakdown</div>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.thLabel}></th>
              <th style={s.thMane}>Mane (Founding)</th>
              <th style={s.thComp}>Vagaro</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={s.tdLabel}>{r.label}</td>
                <td style={s.tdMane}>{r.mane}</td>
                <td style={s.tdComp}>{r.comp}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={s.callout}>
          <div style={s.calloutLabel}>Real math</div>
          <p style={s.calloutText}>
            Solo esthetician using Vagaro with website + forms + email marketing.<br />
            Vagaro: $45/mo base + $10 website + $10 forms + $10 email = <strong style={{ color: RED }}>$75/mo minimum.</strong><br />
            That's <strong style={{ color: RED }}>$900/year</strong> before you've taken a single booking.<br /><br />
            Mane founding rate: <strong style={{ color: GREEN }}>$29/mo. Everything included. $348/year.</strong>
          </p>
        </div>

        <div style={s.sectionTitle}>Common questions</div>
        {faqs.map((f, i) => (
          <div key={i} style={s.faqItem}>
            <div style={s.faqQ}>{f.q}</div>
            <p style={s.faqA}>{f.a}</p>
          </div>
        ))}

        <div style={s.cta}>
          <div style={s.ctaTitle}>Stop paying for features one at a time.</div>
          <p style={s.ctaSub}>
            50 founding spots. Pay $199 once, then $29/mo locked for life.<br />
            When the spots fill, the price goes to $59/mo — no exceptions.
          </p>
          <Link to="/founding" style={s.btn}>Claim Your Founding Spot</Link>
          <p style={{ color: OFF_WHITE, fontSize: 13, marginTop: 20 }}>
            Questions? <a href="mailto:christiangflairbiz@gmail.com" style={{ color: GOLD }}>Email Christian directly.</a> He answers.
          </p>
        </div>
      </div>
    </div>
  );
}
